
import {
    defineComponent,
    onBeforeMount,
    onBeforeUnmount,
    PropType,
    reactive,
    Ref,
    ref,
    watch,
} from 'vue'

// components
import Loader from '@/common/components/UI/Loader/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

// module components
import SendersListItem from '@/modules/CreateOrderModule/components/SendersListItem/index.vue'
import TemplatesActions from '@/modules/CreateOrderModule/components/TemplatesActions/index.vue'
import AdditionalServices from '@/modules/CreateOrderModule/components/AdditionalServices/index.vue'

// types
import { SenderPayloadT, SenderT } from '@/core/types/senders.types'
import { AdditionalServicesI } from '@/core/types/additional_services.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { senders } from '@/core/realisations/senders.realisation'

// composable
import { useDebounce } from 'vue-composable'
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        loader: Loader,
        'the-button': TheButton,
        'the-input': TheInput,
        //
        'senders-list-item': SendersListItem,
        'templates-actions': TemplatesActions,
        'additional-services': AdditionalServices,
    },
    props: {
        companyId: {
            type: Number,
            default: null,
        },
        cityId: {
            type: Number,
            default: null,
        },
        opened: {
            type: Boolean,
            default: false,
        },
        stepNumber: {
            type: Number,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        excelMultipleOrder: {
            type: Boolean,
            default: false,
        },
        isAdditionalServicesVisible: {
            type: Boolean,
            default: false,
        },
        isExpress: {
            type: Boolean,
            default: false,
        },
        additionalService: {
            type: Array as PropType<string[]>,
            default: () => [],
        },
        selectedSender: {
            type: Object as PropType<SenderT>,
            default: null,
        },
    },
    emits: ['submit', 'reset', 'set-additional-services', 'reset-additional-services', 'excel-multiple-order'],
    setup(props, { emit }) {
        const state = reactive({
            is_sender_selected: false,
            sender: null as SenderT | null,
            //
            search_query: null as string | null,
            //
            is_additional_services_opened: true,
            is_additional_services_setted: false,
        })

        const data_state = reactive({
            senders: new DataState<SenderT>({}),
        })

        const loadSenders = async (page: number) => {
            if (!props.companyId) return

            data_state.senders.pagination.current_page = page

            try {
                data_state.senders.is_loading = true

                const response = await senders.getList(page, props.companyId, props.cityId)
                data_state.senders.list = response.data.senders
                data_state.senders.pagination.total_pages = response.data.total_pages

                data_state.senders.is_failed = false
                data_state.senders.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.senders.failure.catchError(error)

                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_failed = true
                }
            } finally {
                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_loading = false
                }
            }
        }

        // infinite scroll
        const loadMoreSenders = async (page: number) => {
            if (page > data_state.senders.pagination.total_pages) return
            data_state.senders.pagination.current_page = page

            try {
                data_state.senders.is_loading = true

                const response = await senders.getList(page, props.companyId, props.cityId)
                data_state.senders.list = data_state.senders.list.concat(response.data.senders)
                data_state.senders.pagination.total_pages = response.data.total_pages

                data_state.senders.is_failed = false
                data_state.senders.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.senders.failure.catchError(error)

                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_failed = true
                }
            } finally {
                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_loading = false
                }
            }
        }

        const templatewindow: Ref<HTMLElement | null> = ref(null)

        const handleScroll = () => {
            if (!templatewindow.value || data_state.senders.is_loading) return

            const scrollHeight = Math.floor(templatewindow.value.scrollHeight)
            const scrollTop = Math.floor(templatewindow.value.scrollTop)
            const offsetHeight = Math.floor(templatewindow.value.offsetHeight)

            if (scrollTop >= scrollHeight - offsetHeight) {
                loadMoreSenders(data_state.senders.pagination.current_page + 1)
            }
        }

        onBeforeUnmount(() => {
            if (!templatewindow.value) return
            templatewindow.value.removeEventListener('scroll', handleScroll)
        })

        const searchSender = useDebounce(async () => {
            data_state.senders.pagination.current_page = 1
            data_state.senders.pagination.total_pages = 1
            data_state.senders.list = []

            if (!state.search_query || state.search_query === '') {
                await loadSenders(1)
                return
            }

            try {
                data_state.senders.is_loading = true

                if (props.cityId) {
                    const response = await senders.searchSender(
                        state.search_query,
                        props.companyId,
                        props.cityId
                    )
                    data_state.senders.list = response.data
                } else {
                    const response = await senders.searchSender(state.search_query, props.companyId)
                    data_state.senders.list = response.data
                }

                data_state.senders.is_failed = false
                data_state.senders.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.senders.failure.catchError(error)

                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_failed = true
                }
            } finally {
                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_loading = false
                }
            }
        }, 800)

        watch(
            () => props.opened,
            (opened) => {
                if (opened) {
                    loadSenders(1)
                }

                if (!opened) {
                    state.search_query = null
                    data_state.senders.pagination.current_page = 1
                    data_state.senders.pagination.total_pages = 1
                    data_state.senders.list = []

                    state.sender = null
                    state.is_sender_selected = false

                    state.is_additional_services_opened = false
                    state.is_additional_services_setted = false
                    emit('reset-additional-services')
                }
            }
        )

        watch(
            () => templatewindow.value,
            (templatewindow_newest) => {
                if (templatewindow_newest) {
                    if (!templatewindow.value) return
                    templatewindow.value.addEventListener('scroll', handleScroll)
                }
            }
        )

        const onSenderSelect = (sender: SenderT) => {
            state.sender = sender
            state.is_sender_selected = true

            emit('submit', { ...sender })
        }

        const emitReset = () => {
            state.sender = null
            state.is_sender_selected = false

            data_state.senders.pagination.current_page = 1
            data_state.senders.pagination.total_pages = 1
            data_state.senders.list = []

            loadSenders(1)

            emit('reset')
        }

        //
        const { actions_state, startCreate, startUpdate } = useActions<SenderT>()
        const notifications = useNotifications()

        const onSenderCreate = async (payload: SenderPayloadT) => {
            try {
                actions_state.is_actions_loading = true

                await senders.createSender(payload)
                notifications.notify({ title: 'Отправитель создан', type: 'success' })

                await loadSenders(1)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Ошибка при создании отправителя' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onSenderUpdate = async (payload: SenderPayloadT) => {
            try {
                actions_state.is_actions_loading = true

                if (!payload.id) return

                await senders.updateSender(payload.id, payload)
                notifications.notify({ title: 'Отправитель отредактирован', type: 'success' })

                await loadSenders(1)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Ошибка при редактировании отправителя' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onSenderDeleted = () => {
            loadSenders(data_state.senders.pagination.current_page)
        }

        const preselectSender = () => {
            onSenderSelect(props.selectedSender)
        }

        onBeforeMount(async () => {
            await loadSenders(1)

            if (props.selectedSender) {
                preselectSender()
            }
        })

        const openAdditionalServices = () => {
            state.is_additional_services_opened = true
            state.is_additional_services_setted = false
        }

        const onAdditionalServicesSubmit = (additional_services: AdditionalServicesI) => {
            emit('set-additional-services', { ...additional_services })

            state.is_additional_services_setted = true
            state.is_additional_services_opened = false
        }

        const onAdditionalServicesReset = () => {
            emit('reset-additional-services')

            state.is_additional_services_setted = false
            state.is_additional_services_opened = false
        }

        const onExcelMultipleOrder = () => {
            if (props.disabled) return
            emit('excel-multiple-order')
        }

        return {
            state,
            data_state,
            loadSenders,
            searchSender,
            onSenderSelect,
            emitReset,
            //
            actions_state,
            startCreate,
            startUpdate,
            //
            onSenderCreate,
            onSenderUpdate,
            onSenderDeleted,
            templatewindow,
            //
            openAdditionalServices,
            onAdditionalServicesSubmit,
            onAdditionalServicesReset,
            onExcelMultipleOrder,
        }
    },
})
